import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';

import type { FeedbackAlertProps } from '@/components';

/** Parent-owned alert state; `onDismiss` is injected by {@link useFeedbackAlert}. */
export type FeedbackAlertState = Omit<FeedbackAlertProps, 'onDismiss'>;

type UseFeedbackAlertReturn = {
  feedback: FeedbackAlertState | undefined;
  setFeedback: Dispatch<SetStateAction<FeedbackAlertState | undefined>>;
  clearFeedback: () => void;
  /** Props to spread onto {@link FeedbackAlert}, including `onDismiss`. */
  alertProps: FeedbackAlertProps;
};

export const useFeedbackAlert = (): UseFeedbackAlertReturn => {
  const [feedback, setFeedback] = useState<FeedbackAlertState | undefined>();

  const clearFeedback = useCallback(() => {
    setFeedback(undefined);
  }, []);

  const alertProps = useMemo(
    (): FeedbackAlertProps => ({
      ...feedback,
      onDismiss: clearFeedback,
    }),
    [clearFeedback, feedback]
  );

  return { feedback, setFeedback, clearFeedback, alertProps };
};
